import LoggerService from "@bfx/logger";
import {
  LOG_TIME_FORMAT,
  LogMessageTimeType,
  MAX_TIME_INTERVAL,
  SHORT_ID_LENGTH,
} from "shared/broker-client-services/worker/CubiqLogger.constants";
import { CubiqLoggerConfig } from "shared/broker-client-services/worker/CubiqLoggerConfig";

import { BMTimeCode, ConcreteEncodable, ErrorsImpl, moment } from "shared/cubiq-model-multi-asset";
const logger = LoggerService.for(__dirname);

/**
 * Base class for logging messages and responses -allows sharing of common config and common logging
 * methods
 */
export class BaseCubiqLogger {
  /** Time the last log message was printed - used when MAX_INTERVAL logging is user */
  protected lastLogTime: number = 0;

  /** Msg ID prefix logged out on next log() call */
  protected currentMessageId: string = "";

  /** Set of types that we have already logged as not supported */
  protected unSupportedTypes: Set<string> = new Set();

  /** Set of types that we have already logged as not handled */
  protected unHandledTypes: Set<string> = new Set();
  /**
   *
   * @param logPrefix Prefix used for all log messages
   * @param config Configuration used to determine log output
   */
  constructor(protected readonly logPrefix: string, protected readonly config: CubiqLoggerConfig) {}

  /**
   * Extract any messageId property from the incoming message and store for later logging
   * @param payload
   */
  protected updateMessageId(payload: ConcreteEncodable) {
    if (this.config.showMessageId && payload && typeof payload["messageId"] === "string") {
      // Message has a messageId field so store for later logging
      let id: string = payload["messageId"];
      if (this.config.showShortMsgId) {
        id = id.substring(0, SHORT_ID_LENGTH);
      }
      this.currentMessageId = ` - msgid:${id}`;
    } else {
      this.currentMessageId = "";
    }
  }

  //********************************************************************** */
  // Helper methods

  /**
   * Translate fixing timer into simple string and
   * @param timeCode
   * @param parts
   */
  protected addFixingCode(timeCode: BMTimeCode, parts: string[]) {
    const fixingCode = this.getFixingCode(timeCode);
    parts.push(fixingCode);
  }

  /**
   * Translate fixing timer into simple string
   *
   * @param timeCode
   * @returns
   */
  protected getFixingCode(timeCode: BMTimeCode): string {
    if (timeCode) {
      return `[${timeCode.code}-${timeCode.time.format("HH:mm")}]`;
    } else {
      return "[<No Fixing Time]";
    }
  }

  /**
   * Get all error messages from the Errors and add them, as a
   * single string, to the parts array **only if errors are found**
   * Note: No translation is done and error args are discarded
   * @param errors
   * @param parts
   */
  protected addErrorParts(errors: ErrorsImpl, parts: string[]) {
    if (errors !== null) {
      const errorMsgs = this.getErrorMessages(errors);
      if (errorMsgs.length > 0) {
        parts.push(`Errors: ${errorMsgs.join("\n,")}\n`);
      }
    }
  }

  /**
   * Translate the error into a single newline-separated string. If there is no error
   * then an empty string is returned.
   * Note: No translation is done and error args are discarded
   * @param errors
   * @returns
   */
  protected getErrorParts(errors: ErrorsImpl): string {
    if (errors !== null) {
      const errorMsgs = this.getErrorMessages(errors);
      return errorMsgs.join("\n,");
    }
    return "";
  }

  // Copied the 2 methods below from bfx-error-handling service as we can't ewasily inject the service into this class

  /**
   * Translate all errors in Erros object and return as array of
   * individual message
   * @param errors
   * @returns Array of string - will be empty if no errors found
   */
  protected getErrorMessages(errors: ErrorsImpl): string[] {
    const retMessages = [];
    if (errors?.errorMessages?.length > 0) {
      this.convertErrorsToStrArray(errors.errorMessages as any[], retMessages);
    }
    return retMessages;
  }

  /**
   * Creates a translation object with a message as id and
   * parameters as args list from a string
   * @param error the error string as served in ErrorsImpl object - a json str
   * @param translations container for translated strings
   */
  private convertErrorsToStrArray(errors: string[], translations: string[]) {
    errors
      .map((e) => JSON.parse(e))
      .forEach((errMessage) => {
        const id: string = errMessage["id"];

        // don't normally want to persist args as don't know what's in them
        const args: any[] = this.config.showErrorArgs ? errMessage["args"] : [];

        if (id && id.length > 0) {
          translations.push(`${id}[${args ? args.join(",") : ""}]`);
        } else {
          logger.out.warn(`eh-s - Cannot find error Id in json: ${errMessage.toString()}`);
        }
      });
  }

  protected getLastClassPart(typeName: string): string {
    if (typeName) {
      const lastDot = typeName.lastIndexOf(".");
      return lastDot !== -1 ? typeName.substring(lastDot + 1) : typeName;
    }
    return "";
  }

  /**
   * Log out a message with logger prefix and optional time
   * @param message
   */
  protected log(message: string) {
    let timeStr: string = this.getTimeStr();
    logger.out.info(`${timeStr}${this.logPrefix} - ${message}${this.currentMessageId}`);
    this.currentMessageId = "";
  }

  private getTimeStr() {
    let timeStr: string = "";
    const now = moment();

    switch (this.config.logMessageTimeType) {
      case LogMessageTimeType.NONE:
        // No time
        timeStr = "";
        break;
      case LogMessageTimeType.MESSAGE:
        // Individual time for each log message
        timeStr = `now.format(LOG_TIME_FORMAT) `;
        break;
      case LogMessageTimeType.MAX_INTERVAL:
        // Time only if MAX_INTERVAL has eleapsed since last time time was output
        const lastTime = this.lastLogTime;
        const nowTime = now.valueOf();
        if (nowTime > lastTime + MAX_TIME_INTERVAL) {
          this.lastLogTime = nowTime;
          timeStr = `${this.logPrefix} - Time: ${now.format(LOG_TIME_FORMAT)}\n`;
        }
    }
    return timeStr;
  }

  protected alert(message: string) {
    logger.out.error(`${this.logPrefix} - ${message}`);
  }

  /**
   *
   * @param source Normally one of MESSAGE, REQUEST, RESPONSE, or RESPONSE-LIST
   * @param type The unknown payload type
   * @returns true if log message is output, false if logMessage swallowed
   */
  protected logUnsupported(source: string, type: string): boolean {
    const id = `${source}:${type}`;
    if (!this.unSupportedTypes.has(id)) {
      // Only ever log out once per type
      this.unSupportedTypes.add(id);
      logger.out.warn(
        `${this.logPrefix} - ${source} - Unsupported logging type ${type} - a default value for this should be defined`,
      );
      return true;
    }
    return false;
  }

  /**
   * Called when an incoming message has no registered handlers
   * @param source
   * @param type
   */
  public logUnhandled(source: string, type: string) {
    const id = `${source}:${type}`;
    if (!this.unHandledTypes.has(id)) {
      // Only ever log out once per type
      this.unHandledTypes.add(id);
      logger.out.warn(
        `${this.logPrefix} - ${source} - no message handling for message of type ${type}`,
      );
    }
  }
}
